import { pass1LearningPath, Pass1LearningPath } from '../../typesV2';
import { z } from 'zod';

const Pass2SidequestSchema = z.object({
  sideQuestTitle: z.string(),
  sideQuestDescription: z.string(),
});

const Pass2SectionSchema = z.object({
  subTopicTitle: z.string(),
  sectionDescription: z.string(),
});

const pass2OptionSchema = z.object({
  optionTitle: z.string(),
  optionDescription: z.string(),
  optionTag: z.string().optional(),
});

const pass2TopicSchema = z.object({
  topicTitle: z.string(),
  topicDescription: z.string(),
  type: z.enum(['normal', 'pluralistic']),
  sections: z.array(Pass2SectionSchema).optional(),
  options: z.array(pass2OptionSchema).optional(),
  sideQuests: z.array(Pass2SidequestSchema).optional(),
});

const learningPath = z.object({
  topic1Title: z.literal('Advanced Automotive Technologies'),
});

const pass2LearningPath = z.array(pass2TopicSchema);
type Pass2LearningPath = z.infer<typeof pass2LearningPath>;

//************************************************************ */
// Type for option content within pluralistic sections
type OptionContent = {
  optionDescription: string;
  optionTag: string;
};

// Type for a single pluralistic section item (with option titles as keys)
type PluristicSectionItem = {
  [optionTitle: string]: OptionContent;
};

// Type for all pluralistic sections (with section titles as keys)
type PluristicSections = {
  [sectionTitle: string]: PluristicSectionItem[];
};

// Type for a sidequest in normal sections
type SideQuest = {
  sideQuestTitle: string;
  sideQuestDescription: string;
};

// Type for all normal sections (with section titles as keys)
type NormalSectionsSidequests = {
  [sectionTitle: string]: SideQuest[];
};

// The complete schema type
export type SchemaOutput = {
  pluralisticSections: PluristicSections;
  normalSectionsSidequests: NormalSectionsSidequests;
};

export const mergeEmbellishedSectionsIntoLearningPath = (
  pass1LearningPath: Pass1LearningPath,
  partialData: Partial<SchemaOutput>
): Pass2LearningPath => {
  // Cast to complete object with empty defaults
  const pass2LearningPath: SchemaOutput = {
    pluralisticSections: partialData.pluralisticSections || {},
    normalSectionsSidequests: partialData.normalSectionsSidequests || {},
  };

  // Initialize counters for normal and pluralistic sections
  let normalSectionCount = 0;
  let pluralisticSectionCount = 0;

  // Get arrays of keys for both section types
  const normalSectionKeys = Object.keys(
    pass2LearningPath.normalSectionsSidequests || {}
  );
  const pluralisticSectionKeys = Object.keys(
    pass2LearningPath.pluralisticSections || {}
  );

  // Create a new array to hold the merged result
  const mergedPath: Pass2LearningPath = [];

  // Loop through each section in the original path
  for (const section of pass1LearningPath) {
    if (section.type === 'normal') {
      // We need the nth normal section
      const currentKey = normalSectionKeys[normalSectionCount];

      // Check if data for this section exists yet
      if (
        !currentKey ||
        !pass2LearningPath.normalSectionsSidequests?.[currentKey]
      ) {
        // Data not available yet, return what we have so far plus remaining unprocessed sections
        const remainingPath = [
          ...mergedPath,
          section,
          ...pass1LearningPath.slice(pass1LearningPath.indexOf(section) + 1),
        ] as Pass2LearningPath;
        return remainingPath;
      }

      // Merge the section with embellishments
      mergedPath.push({
        ...section,
        sideQuests: pass2LearningPath.normalSectionsSidequests[currentKey],
        // Ensure options have required properties
        options: section.options?.map((opt) => ({
          ...opt,
          optionDescription: ('optionDescription' in opt
            ? String(opt.optionDescription)
            : '') as string,
          optionTag: ('optionTag' in opt
            ? String(opt.optionTag)
            : '') as string,
        })),
        // Ensure sections have required properties
        sections: section.sections?.map((sec) => ({
          ...sec,
          sectionDescription: ('sectionDescription' in sec
            ? String(sec.sectionDescription)
            : '') as string,
        })),
      });

      normalSectionCount++;
    } else if (section.type === 'pluralistic') {
      // We need the nth pluralistic section
      const currentKey = pluralisticSectionKeys[pluralisticSectionCount];

      // Check if data for this section exists yet
      if (!currentKey || !pass2LearningPath.pluralisticSections?.[currentKey]) {
        // Data not available yet, return what we have so far plus remaining unprocessed sections
        const remainingPath = [
          ...mergedPath,
          section,
          ...pass1LearningPath.slice(pass1LearningPath.indexOf(section) + 1),
        ] as Pass2LearningPath;
        return remainingPath;
      }

      // For pluralistic sections, we need to merge option details
      const optionsWithEmbellishments = section.options?.map((option) => {
        // Find the matching option in embellished data
        const embellishedItems =
          pass2LearningPath.pluralisticSections[currentKey];

        // Try to find the matching item containing this option
        for (const item of embellishedItems || []) {
          const embellishedOption = item[option.optionTitle];
          if (embellishedOption) {
            return {
              ...option,
              optionDescription: embellishedOption.optionDescription,
              optionTag: embellishedOption.optionTag,
            };
          }
        }

        // If no match found, return option with default values for required fields
        return {
          ...option,
          optionDescription:
            'optionDescription' in option ? option.optionDescription : '',
          optionTag: 'optionTag' in option ? option.optionTag : '',
        };
      });

      // Add to merged path
      mergedPath.push({
        type: section.type,
        topicTitle: section.topicTitle,
        topicDescription: section.topicDescription,
        options: optionsWithEmbellishments as {
          optionTitle: string;
          optionDescription: string;
          optionTag: string;
        }[],
        sections: section.sections?.map((sec) => ({
          ...sec,
          sectionDescription: ('sectionDescription' in sec
            ? String(sec.sectionDescription)
            : '') as string,
        })),
      });

      pluralisticSectionCount++;
    } else {
      // Unknown section type, just pass through
      mergedPath.push(section as any);
    }
  }

  return mergedPath;
};
